import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as RadixSlider from '@radix-ui/react-slider';
import * as RadixSwitch from '@radix-ui/react-switch';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { Check, Minus } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';

/* ───── Checkbox ───── */

export interface CheckboxProps {
  checked: boolean | 'indeterminate';
  onCheckedChange: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  className?: string;
  /** Sitting on a photo or a video frame: see the `onMedia` button variant for why. */
  onMedia?: boolean;
  'aria-label'?: string;
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  className,
  onMedia,
  ...props
}: CheckboxProps) {
  const id = useId();
  return (
    <div className={cn('flex items-start gap-2', className)}>
      <RadixCheckbox.Root
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={disabled}
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded border',
          onMedia
            ? 'border-white/60 bg-black/55 backdrop-blur-sm'
            : 'border-border-strong bg-bg-subtle',
          'transition-colors duration-150 hover:border-accent',
          'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
          'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...props}
      >
        <RadixCheckbox.Indicator className="text-accent-fg">
          {checked === 'indeterminate' ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" />}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      {label && (
        <label htmlFor={id} className="cursor-pointer select-none text-base leading-tight">
          {label}
          {description && <span className="mt-0.5 block text-xs text-fg-subtle">{description}</span>}
        </label>
      )}
    </div>
  );
}

/* ───── Switch ───── */

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export function Switch({ checked, onCheckedChange, label, description, disabled, className }: SwitchProps) {
  const id = useId();
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      {label && (
        <label htmlFor={id} className="cursor-pointer select-none text-base">
          {label}
          {description && <span className="mt-0.5 block text-xs text-fg-subtle">{description}</span>}
        </label>
      )}
      <RadixSwitch.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={cn(
          'relative h-5 w-9 shrink-0 cursor-pointer rounded-full border border-border-strong bg-surface-hover',
          'transition-colors duration-150',
          'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <RadixSwitch.Thumb
          className={cn(
            'block h-3.5 w-3.5 translate-x-0.5 rounded-full bg-fg-muted shadow-sm',
            'transition-transform duration-150 will-change-transform',
            'data-[state=checked]:translate-x-[1.125rem] data-[state=checked]:bg-white',
          )}
        />
      </RadixSwitch.Root>
    </div>
  );
}

/* ───── Slider ───── */

export interface SliderProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  /** Formatted readout shown next to the label, e.g. "35%" or "1.2s". */
  display?: string;
  disabled?: boolean;
  className?: string;
  /** Required when there is no visible `label`. */
  'aria-label'?: string;
}

export function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  display,
  disabled,
  className,
  'aria-label': ariaLabel,
}: SliderProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || display) && (
        <div className="flex items-center justify-between text-sm">
          {label && <span className="font-medium text-fg">{label}</span>}
          {display && <span className="tabular-nums text-fg-muted">{display}</span>}
        </div>
      )}
      <RadixSlider.Root
        value={[value]}
        onValueChange={([next]) => onValueChange(next ?? min)}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className="relative flex h-4 w-full cursor-pointer touch-none select-none items-center disabled:opacity-50"
      >
        <RadixSlider.Track className="relative h-1 w-full grow rounded-full bg-surface-hover">
          <RadixSlider.Range className="absolute h-full rounded-full bg-accent" />
        </RadixSlider.Track>
        {/* The thumb is the element with role="slider", so the name has to live here —
            on the Root it never reaches assistive technology. */}
        <RadixSlider.Thumb
          aria-label={label ?? ariaLabel}
          aria-valuetext={display}
          className="block h-3.5 w-3.5 rounded-full border-2 border-accent bg-surface shadow-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </RadixSlider.Root>
    </div>
  );
}

/* ───── Tooltip ───── */

export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return <RadixTooltip.Provider delayDuration={400}>{children}</RadixTooltip.Provider>;
}

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  if (!content) return <>{children}</>;
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          className={TOOLTIP_CONTENT}
        >
          {content}
          <RadixTooltip.Arrow className="fill-[rgb(var(--surface-raised))]" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

// max-w + break-words: tooltips carry full captions and URLs, which must wrap, not run off-screen.
const TOOLTIP_CONTENT =
  'z-[70] max-w-[min(22rem,calc(100vw-1rem))] animate-fade-in whitespace-pre-line break-words rounded border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-md';

/**
 * The full text of a truncated element, on hover and focus — and only when it actually is
 * truncated. It replaces the `title` attribute, whose tooltip is the browser's own (a pale
 * system box after a long delay) and which repeats text that is already fully visible.
 */
export function OverflowTooltip({
  content,
  children,
  side = 'top',
}: {
  content: React.ReactNode;
  /** Exactly one element: the one that truncates (`truncate` / `line-clamp-*`). */
  children: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  // Typed as a button because that is what Radix declares the trigger to be; with asChild it is
  // whatever element the child is, and only its scroll/client sizes are read.
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (!content) return children;
  return (
    <RadixTooltip.Root
      open={open}
      onOpenChange={(next) => {
        const element = ref.current;
        const clipped =
          element !== null &&
          (element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1);
        setOpen(next && clipped);
      }}
    >
      <RadixTooltip.Trigger asChild ref={ref}>
        {children}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content side={side} sideOffset={6} className={TOOLTIP_CONTENT}>
          {content}
          <RadixTooltip.Arrow className="fill-[rgb(var(--surface-raised))]" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

/* ───── Progress ───── */

export interface ProgressBarProps {
  /** 0..1. Ignored when `indeterminate` is set. */
  value?: number;
  indeterminate?: boolean;
  className?: string;
  label?: string;
}

/**
 * When the backend cannot report real progress (translate/ASR/TTS/shorts jump 0 → 1) the bar
 * must be indeterminate. Inventing a percentage there would be lying to the user.
 */
export function ProgressBar({ value = 0, indeterminate, className, label }: ProgressBarProps) {
  const percent = Math.round(Math.min(Math.max(value, 0), 1) * 100);
  return (
    <div
      className={cn('h-1 w-full overflow-hidden rounded-full bg-surface-hover', className)}
      role="progressbar"
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
      aria-valuenow={indeterminate ? undefined : percent}
      aria-label={label}
    >
      {indeterminate ? (
        <div className="h-full w-1/3 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-accent" />
      ) : (
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      )}
    </div>
  );
}
